<script setup lang="ts">
interface Props {
  title: string
  isLoading?: boolean
}

interface Emits {
  (e: 'update:title', value: string): void
}

const props = withDefaults(defineProps<Props>(), {
  isLoading: false
})

const emit = defineEmits<Emits>()

const { t } = useI18n()

const isEditingName = ref(false)
const editedAudienceName = ref('')
const editNameInputRef = ref<HTMLInputElement | null>(null)
const pageTitleName = ref<HTMLHeadingElement | null>(null)
const shouldCancelEdit = ref(false)
const inputWidth = ref('auto')

const startEditingName = () => {
  if (pageTitleName.value) {
    inputWidth.value = `${pageTitleName.value.offsetWidth + 20}px`
  }
  editedAudienceName.value = props.title
  isEditingName.value = true

  setTimeout(() => {
    editNameInputRef.value?.focus()
  }, 100)
}

const handleEditAudienceName = () => {
  if (shouldCancelEdit.value) {
    shouldCancelEdit.value = false
    return
  }

  if (!editedAudienceName.value.trim() || editedAudienceName.value === props.title) {
    isEditingName.value = false
    editedAudienceName.value = props.title
    return
  }

  emit('update:title', editedAudienceName.value)
  isEditingName.value = false
}

const cancelEditingName = () => {
  shouldCancelEdit.value = true
  isEditingName.value = false
  editedAudienceName.value = props.title
}

defineExpose({
  startEditingName
})

watch(
  () => props.title,
  (newTitle) => {
    if (!isEditingName.value) {
      editedAudienceName.value = newTitle
    }
  }
)
</script>

<template>
  <div v-if="isEditingName" class="flex items-center gap-2 w-full">
    <input
      ref="editNameInputRef"
      v-model="editedAudienceName"
      type="text"
      class="text-2xl rounded-md font-bold tracking-tight text-foreground border-b-2 border-primary focus:outline-none focus:border-primary bg-transparent px-1 py-0 w-full"
      :style="{
        width: inputWidth
      }"
      @keyup.enter="handleEditAudienceName"
      @keyup.escape="cancelEditingName"
    />
    <button
      type="button"
      class="text-green-600 hover:text-green-700 rounded-md p-1 hover:bg-green-50 cursor-pointer"
      :title="t('global.words.save')"
      @click="handleEditAudienceName"
    >
      <BaseIcon class="w-5 h-5" name="HeroCheckIconSolid" />
    </button>
    <button
      type="button"
      class="text-muted-foreground hover:text-foreground-secondary rounded-md p-1 hover:bg-accent cursor-pointer"
      :title="t('global.words.cancel')"
      @click="cancelEditingName"
    >
      <BaseIcon class="w-5 h-5" name="HeroXMarkIconSolid" />
    </button>
  </div>
  <div v-else class="flex items-center gap-2 group">
    <h1 ref="pageTitleName" class="text-2xl font-bold tracking-tight text-foreground sm:text-2xl">
      {{ title }}
    </h1>
  </div>
</template>
